/**
 * English dictionary — the canonical key set. `bn.ts` must satisfy
 * `Dictionary` (derived from this object), so a missing translation is a
 * compile error, not a runtime surprise.
 */
export const en = {
    common: {
        open: "Open",
        appName: "DrinkPure",
        skipToContent: "Skip to content",
        tagline: "Safe water for everyone",
        loading: "Loading…",
        retry: "Try again",
        cancel: "Cancel",
        confirm: "Confirm",
        back: "Back",
        continue: "Continue",
        updatedJustNow: "Updated just now",
        safeToRetry: "Safe to retry — this won’t happen twice.",
        change: "change",
        orContinueWith: "or continue with",
        orSignUpWith: "or sign up with",
        comingSoon: "Coming here soon.",
    },
    nav: {
        home: "Home",
        activity: "Activity",
        recharge: "Recharge",
        cards: "Cards",
        profile: "Profile",
    },
    welcome: {
        valueBalanceTitle: "Check your balance anywhere",
        valueBalanceBody: "Your wallet and card, always in your pocket",
        valueRechargeTitle: "Recharge with bKash, Nagad or card",
        valueRechargeBody: "In under a minute, with no extra fee",
        valueHonestTitle: "Pay only for water you receive",
        valueHonestBody: "No water means no charge — ever",
        createAccount: "Create account",
        logIn: "Log in",
        // {price} / {fee} are filled from runtime config via formatMoney.
        priceLine: "Water price: {price} per litre · Card pack {fee}",
    },
    auth: {
        // shared
        phone: "Phone",
        email: "Email",
        phoneNumber: "Phone number",
        emailAddress: "Email address",
        password: "Password",
        phoneHelp: "The number you’ll use at the booth and for SMS receipts",
        emailHelp: "You can add your booth phone number later for SMS receipts",
        phoneOrEmail: "Phone or email",
        phoneOrEmailHelp: "Whichever is on your account",
        show: "Show",
        hide: "Hide",
        haveAccount: "Already have an account?",
        noAccount: "New to DrinkPure?",
        termsNote: "By continuing you agree to the DrinkPure terms.",
        // register steps
        createTitle: "Create account",
        stepPhone: "Phone",
        stepCode: "Code",
        stepPassword: "Password",
        sendCode: "Send code",
        // otp
        enterCodeTitle: "Enter the code",
        codeSentSms: "We sent a 6-digit code by SMS to",
        codeSentEmail: "We sent a 6-digit code by email to",
        verify: "Verify",
        codeExpiresIn: "Code expires in",
        resendIn: "Resend in",
        resendCode: "Resend code",
        devCodeNote: "Dev code (staging only):",
        // password
        setPasswordTitle: "Set your password",
        passwordIntro: "You’ll log in with your phone number or email and this password. A code is only needed for sign-up and reset.",
        ruleLength: "At least 10 characters",
        ruleNotGuessable: "Not an easily guessed password",
        ruleTip: "Tip: a short phrase you’ll remember works best",
        // login
        loginTitle: "Log in",
        forgotPassword: "Forgot password?",
        forgotHint: "we’ll send you a code",
        // forgot
        forgotTitle: "Reset password",
        forgotIntro: "Enter your phone or email and we’ll send a code to reset your password.",
        newPassword: "New password",
        resetPassword: "Reset password",
        resetSignsOut: "Resetting logs you out of all other devices.",
        done: "Done",
    },
    home: {
        myWallet: "My wallet",
        greeting: "Welcome",
        availableBalance: "Available balance",
        quickRecharge: "Recharge",
        quickActivate: "Activate card",
        recentActivity: "Recent activity",
        viewAll: "View all",
        // {amount} filled via formatMoney.
        pendingProcessing: "Recharge of {amount} is processing",
        pendingView: "View",
        frozenTitle: "This account is frozen",
        frozenBody: "Your balance is safe. Contact support to continue using it.",
        frozenChip: "Frozen",
        heldSafely: "Balance — held safely",
        contactSupport: "Contact support",
        newUserTitle: "No wallet yet",
        newUserBody: "Buy a sealed pack for Tk 50 at any booth and activate it here — activating the card creates your wallet.",
        newUserCta: "Activate my card",
        boothTitle: "Where’s a booth?",
        boothBody: "Look for the DrinkPure dispenser at markets, schools and clinics near you.",
    },
    wallet: {
        detailTitle: "Wallet",
        fundedTitle: "How your balance is funded",
        fundedNote: "Free credits are used before the money you paid.",
        spendsFirst: "spends first",
        linkedCard: "Linked card",
        rechargeCta: "Recharge",
        activityCta: "Activity",
        holdBannerTitle: "A dispense is in progress",
        // {amount} filled via formatMoney.
        holdBannerBody: "{amount} is briefly held for a dispense in progress — it returns automatically within 3 minutes.",
        holdTotal: "Total balance",
        holdHeld: "Held for dispense",
        holdAvailable: "Available now",
        blockedTitle: "The card is blocked, your money isn’t.",
        blockedBody: "Your wallet stays active and can still be recharged. Activating a replacement card reconnects it to this same balance.",
        replaceCard: "Replace card",
        viewCard: "View card",
        cardActive: "Active",
        cardBlocked: "Card blocked",
        cardLost: "Card lost",
        cardReplaced: "Card replaced",
        source: {
            promotion: "Promotion credit",
            goodwill: "Goodwill credit",
            correction: "Correction credit",
            refund: "Refund",
            reversal: "Reversal",
            paid: "You paid",
            other: "Other credit",
        },
    },
    cards: {
        // ---- list ----
        listTitle: "My cards",
        activate: "Activate a card",
        walletPrefix: "Wallet",
        safeSuffix: "safe",
        manage: "Tap to manage",
        needTitle: "Need a card?",
        // {fee} filled via formatMoney.
        needBody: "Buy a sealed DrinkPure pack for {fee} at any booth. Inside you’ll find the card, its number and a one-time activation code.",
        // ---- activate form ----
        activateTitle: "Activate card",
        sealed: "Sealed",
        readyToActivate: "Ready to activate",
        activateIntro: "Type the number and code printed inside your sealed pack.",
        cardNumberLabel: "Card number",
        cardNumberHelp: "Letters I & O and digits 0 & 1 are never used — if it looks like one, it’s the other.",
        cardNumberPlaceholder: "DP__ ____ ____ __",
        codeLabel: "Activation code",
        codePlaceholder: "10 characters",
        replaceToggle: "This replaces a lost or blocked card",
        replaceHint: "Pick the old card — its wallet and balance move to the new card:",
        // {amount} filled via formatMoney.
        replaceCarry: "Balance {amount} — will carry over",
        activateAction: "Activate",
        mode2Title: "No phone handy at the booth?",
        mode2Body: "A new card can also be activated at any dispenser: tap the sealed card, enter your number on the machine, confirm the SMS code — done. (Replacements are app-only.)",
        // field-level validation (client-side, blocks I/O/0/1). {char} is the look-alike.
        errAmbiguous: "Contains “{char}” — 0, 1, I and O are never used.",
        errCardPrefix: "Card numbers start with DP.",
        errCardIncomplete: "That isn’t a full card number yet.",
        errCodeIncomplete: "That isn’t a full activation code yet.",
        // ---- success: new ---- {token} is the safe card token.
        successNewTitle: "{token} is live",
        successNewBody: "A new wallet was created for this card. You’ll get an SMS confirmation.",
        walletBalance: "Wallet balance",
        cardLabel: "Card",
        rechargeNow: "Recharge it now",
        later: "Later",
        // ---- success: replacement ---- {oldToken} old token, {amount} carried.
        successReplaceTitle: "Replacement complete",
        successReplaceBody: "Your old card ({oldToken}) is retired. Your {amount} moved to the new card — same wallet, new key.",
        newCard: "New card",
        balanceCarried: "Balance carried",
        oldCard: "Old card",
        replaced: "Replaced",
        done: "Done",
        // ---- activate errors that route to support (fraud-aware) ---- {support} phone.
        alreadyActiveTitle: "This card is already active.",
        alreadyActiveBody: "If it’s yours, you’ll find it in your cards list. If you bought it sealed and it says this, don’t use it — return it to the booth and call {support}.",
        backToMyCards: "Back to my cards",
        conflictTitle: "This card was already used with different details.",
        conflictBody: "For your security it can’t be reused here. Please contact support at {support}.",
        inProgress: "This activation is still processing — give it a moment and try again.",
        // ---- card detail ----
        detailTitle: "Card",
        historyTitle: "History",
        tlActiveNow: "Active — taps spend your wallet",
        // {date} from activated_at.
        tlSince: "since {date}",
        tlBlockedNow: "Blocked",
        tlLostNow: "Reported lost",
        tlActivated: "Activated in the app",
        lostQTitle: "Lost your card?",
        lostQBody: "Block it the moment you notice — taps stop instantly and your balance stays safe in the wallet.",
        blockAction: "Block card",
        reportLostAction: "Report lost",
        // block confirm modal. {amount} via formatMoney.
        blockConfirmTitle: "Block this card?",
        blockConfirmBody: "Taps at the booth stop immediately. Your {amount} stays safe in the wallet.",
        blockConfirmNote: "Re-enabling later needs DrinkPure support — you can’t unblock from the app.",
        // lost confirm modal.
        lostConfirmTitle: "Report this card lost?",
        lostConfirmBody: "This is permanent — a lost card can’t be re-enabled. Taps stop immediately and your {amount} stays safe. You can move the balance to a new card any time by activating a replacement.",
        // blocked state. {support} phone.
        blockedTitle: "Unblocking needs a person, honestly.",
        blockedBody: "For safety, a blocked card can only be re-enabled by DrinkPure support — not from the app. Call {support} or visit any booth with your phone number.",
        // lost state. {amount} / {fee} via formatMoney.
        lostSafeTitle: "Taps are stopped and your {amount} is safe.",
        lostSafeBody: "Buy a new sealed pack ({fee}) at any booth and activate it as a replacement — the balance moves to the new card.",
        activateReplacement: "Activate replacement card",
    },
    activity: {
        title: "Activity",
        emptyTitle: "No activity yet",
        emptyBody: "Your water purchases and recharges will appear here.",
        loadMore: "Load more",
        footnote: "No charge is ever made without proven delivery.",
        balanceAfter: "Balance after",
        partial: "Partial",
        completed: "Completed",
        today: "Today",
        yesterday: "Yesterday",
        category: {
            waterPurchase: "Water purchase",
            recharge: "Recharge",
            supportCredit: "Support credit",
            reversal: "Reversal",
        },
        // {vol} filled via formatVolume.
        askedFor: "You asked for {vol}",
        received: "You received {vol}",
        machineStopped: "The machine stopped early. You were charged only for the water that actually flowed.",
        fullyDelivered: "The full amount you asked for was delivered.",
        charged: "Charged",
        holdReleased: "Hold released",
        // {amount} + {vol} filled via formatMoney / formatVolume.
        holdReleasedNote: "“Hold released” means {amount} was reserved for the full {vol} but never taken from your wallet — it isn’t a refund, because it never left.",
        receiptRef: "Receipt ref",
        receiptRefGap: "Not available yet",
        reversalExplain: "A debit made in error was corrected. The original entry stays in your history — corrections are always new entries, never edits.",
        reason: "Reason",
        reversalSpend: "This credit is spent before money you paid.",
        close: "Close",
    },
    recharge: {
        // ---- form ----
        title: "Recharge wallet",
        whichWallet: "Which wallet?",
        howMuch: "How much?",
        // {min} / {max} / {litres} filled at render.
        amountHelp: "Min {min} · Max {max} · {amount} ≈ {litres} litres of water",
        payWith: "Pay with",
        providerName: "Mobile banking / card",
        providerHint: "You’ll pick the exact method on the secure payment page",
        // {amount} filled at render.
        payButton: "Pay {amount}",
        feeNote: "No extra fee — DrinkPure pays the gateway charge. Your wallet is credited only after the payment is confirmed.",
        belowMin: "Minimum recharge is {min}",
        aboveMax: "Maximum recharge is {max}",
        inflightTitle: "Your previous attempt is still processing.",
        inflightBody: "Don’t retry yet — nothing will be charged twice; we’re fetching the result.",
        inflightView: "View status",
        gatewayDownTitle: "The payment service isn’t responding right now.",
        gatewayDownBody: "No money was taken. Please try again in a few minutes.",
        walletBlockedCardNote: "card blocked, wallet active",
        // ---- result page ----
        processingTitle: "Confirming your payment…",
        processingBody: "This usually takes a few seconds. Your wallet is credited only when the payment is verified with the bank — never before.",
        // {n} filled at render.
        processingAttempt: "Checking again automatically… (attempt {n})",
        successTitle: "{amount} added",
        successPaidWith: "Paid with {method} · An SMS receipt is on its way.",
        successPaid: "Paid",
        newBalance: "New balance",
        notCreditedTitle: "Payment received — balance updating",
        notCreditedBody: "The gateway confirmed your payment. We’re writing it to your wallet now; the balance updates in a moment.",
        checkAgain: "Check again",
        failedTitle: "Payment didn’t go through",
        failedBody: "Declined by the payment provider. No money left your account — if anything was deducted, the provider reverses it automatically.",
        attempted: "Attempted",
        cancelledTitle: "Recharge cancelled",
        cancelledBody: "You cancelled on the payment page. Nothing was charged.",
        expiredTitle: "This attempt expired",
        expiredBody: "The payment window (30 minutes) passed without a confirmed payment. Nothing was charged. If you did pay and it isn’t reflected within an hour, contact support with the reference below.",
        stuckTitle: "Still confirming…",
        stuckBody: "The bank is taking longer than usual. You don’t need to do anything — if the payment went through, your wallet is credited automatically, and you’ll get an SMS. If it didn’t, nothing is charged.",
        stuckCheckHistory: "Check later in Recharge history",
        loggedOutTitle: "Payment received by the gateway",
        loggedOutBody: "Your session ended during the payment. Log in to see whether the recharge was credited — the result is safe either way.",
        loggedOutLogin: "Log in to see the result",
        missingTitle: "Recharge not found",
        missingBody: "We couldn’t find that recharge. Check your recharge history.",
        amount: "Amount",
        wallet: "Wallet",
        reference: "Reference",
        done: "Done",
        viewActivity: "View activity",
        tryAgain: "Try again",
        startAgain: "Start again",
        startNew: "Start a new recharge",
        backHome: "Back to home",
        copyRef: "Copy reference",
        copied: "Copied",
        // ---- history ----
        historyTitle: "Recharge history",
        statusPending: "Pending",
        statusSuccess: "Success",
        statusFailed: "Failed",
        statusCancelled: "Cancelled",
        statusExpired: "Expired",
        failedMoneyNotTaken: "Declined by provider · money not taken",
        cancelledSub: "You cancelled on the payment page",
        historyFootnote: "A pending recharge resolves automatically — tap it any time to see live status.",
        emptyTitle: "No recharges yet",
        emptyBody: "Top up with bKash, Nagad, Rocket or a card — the balance lands in seconds.",
        rechargeNow: "Recharge now",
    },
    profile: {
        title: "Profile",
        yourAccount: "Your account",
        rechargeHistory: "Recharge history",
        rechargeHistorySub: "Your past top-ups and their status",
        verified: "Verified",
        changePhone: "Change phone number",
        changePhoneSub: "Verified by a code to the new number",
        changePassword: "Change password",
        changePasswordSub: "Reset with a code — other devices get logged out",
        language: "Language",
        languageSub: "Switches instantly",
        help: "Help & prices",
        helpSub: "How charging works, refunds, booth guide",
        logOut: "Log out",
    },
    help: {
        title: "Help & prices",
        pricesTitle: "Water prices",
        perLitre: "Per litre",
        packagesLabel: "1 L · 2 L · 5 L · 10 L",
        newCardPack: "New card pack",
        pricesNote: "Same price at every booth, tax included. The buttons are just shortcuts — you’re always charged litres × price.",
        howTitle: "How charging works",
        howFull: "Full water = full price",
        howPartial: "Machine stops early = you pay only for what flowed",
        howNone: "No water at all = Tk 0 — no charge, and no line in your statement",
        refundTitle: "Cash refunds at the booth",
        refundBody: "Paying cash and got less water? The machine screen shows the refund amount — collect it from the booth staff right away. Any problem later, call the hotline.",
        smsTitle: "When we text you",
        smsBody: "On successful recharge, wallet credits or corrections, card activate/block/lost, and number changes. Not for each water purchase — every transaction is in Activity here.",
        supportTitle: "Support",
        supportHours: "every day 8 AM – 10 PM",
        supportRef: "Quote the transaction reference when you call (e.g. DP-TR-9K3FQ2) for a faster fix.",
    },
    crash: {
        title: "This screen hit a snag",
        body: "Sorry — an unexpected error stopped this page. Your balance and money are safe. Reload to continue; if it keeps happening, call {support}.",
        reload: "Reload",
    },
    errors: {
        // Stable error-code → copy taxonomy. Server `message` is never rendered.
        network: "Can’t reach DrinkPure — check your connection and try again.",
        invalid_credentials: "Phone number, email or password didn’t match.",
        registration_conflict: "This contact already has an account. Log in instead.",
        channel_unavailable: "Email sign-in isn’t available yet — please use your phone number.",
        invalid_otp: "That code didn’t work. Check the latest message — a code expires 5 minutes after it’s sent.",
        otp_expired: "That code has expired. Request a fresh one to continue.",
        weak_password: "This password is too easy to guess — try a longer, personal phrase.",
        completion_expired: "This step timed out. Please verify your contact again — it takes a few seconds.",
        rate_limited: "Too many attempts — please wait a bit and try again.",
        account_locked: "Too many attempts. For your protection, try again in a few minutes.",
        // ---- manage/console auth ----
        totp_invalid: "That code didn’t match. Enter the current 6-digit code from your authenticator app.",
        unauthenticated: "Your session ended. Please sign in again.",
        inactive: "This account isn’t active. Ask an admin to re-enable it.",
        no_credential: "No password is set for this account yet. Ask an admin to set one via the ops CLI.",
        unauthorized: "Your session ended. Please log in again.",
        forbidden: "You don’t have access to that.",
        not_found: "We couldn’t find that.",
        conflict: "That didn’t apply — it may have changed elsewhere. Refresh and retry.",
        amount_below_minimum: "That amount is below the minimum recharge.",
        amount_above_maximum: "That amount is above the maximum recharge.",
        wallet_not_active: "This wallet can’t be recharged right now.",
        payment_provider_unavailable: "The payment service isn’t responding right now. No money was taken — try again shortly.",
        in_progress: "Your previous attempt is still processing — no need to try again.",
        // ---- cards ----
        activation_code_invalid: "That code doesn’t match this card. Double-check the printed code inside the pack.",
        card_not_found: "We couldn’t find a card with that number. Check the printed number inside the pack.",
        card_state_conflict: "That didn’t apply — this card’s status has changed. Refresh and try again.",
        activation_conflict: "This card was already used with different details. Please contact support.",
        activation_in_progress: "This activation is still processing — try again in a moment.",
        replaces_card_not_found: "We couldn’t find the card you’re replacing. Refresh your cards and try again.",
        card_not_owned: "That card isn’t on your account.",
        customer_not_active: "Your account isn’t active right now. Contact support to continue.",
        // ---- firmware ----
        reason_required: "A reason is required.",
        device_not_found: "We couldn’t find that booth.",
        release_not_found: "We couldn’t find that release.",
        rollout_not_found: "We couldn’t find that rollout.",
        rollout_open: "Another rollout is open. Finish or abort it first.",
        rollout_closed: "This rollout has ended; nothing more can be done to it.",
        rollout_not_active: "This rollout is paused. Resume it first.",
        stage_not_done: "The current stage has a booth under way, or has not run its soak time.",
        last_stage: "This rollout is already at its last stage.",
        release_unavailable: "This release can no longer be rolled out: it was deprecated or revoked.",
        release_revoked: "This release was revoked.",
        no_firmware_report: "This booth has not reported its firmware yet.",
        not_revertible: "This booth names no image it can go back to now.",
        revert_pending: "A revert of this booth is already under way.",
        unknown: "Something went wrong on our side. Nothing was charged.",
    },
    // ---- operator console ----
    manage: {
        brand: "DrinkPure",
        consoleTag: "Operator console",
        logout: "Log out",
        refresh: "Refresh",
        environment: "Environment",
        yourRole: "Your role",
        refreshNow: "Refresh data now",
        freshnessIdle: "Live",
        boot: "Operator Console",
        roles: {
            admin: "Admin",
            manager: "Manager",
            technician: "Technician",
            staff: "Staff",
        },
        login: {
            title: "Operator sign-in",
            subtitle: "DrinkPure operations console",
            identifierLabel: "Email or phone",
            identifierHelp: "Admins sign in with email · staff with their phone number",
            passwordLabel: "Password",
            submit: "Continue",
            cliNote: "Passwords are set through the ops CLI for now — there’s no self-service reset yet.",
            sessionNote: "Sessions last 15 minutes and refresh for up to 12 hours. Access is revoked the moment an account is deactivated.",
        },
        totp: {
            verifyTitle: "Two-factor code",
            signedInAs: "Signed in as {name}",
            codeLabel: "6-digit code from your authenticator app",
            validity: "The challenge is valid for 5 minutes",
            verify: "Verify & sign in",
            differentAccount: "← Different account",
            lostAuthenticator: "Lost your authenticator? An admin can reset your 2FA — you’ll re-enrol at next login.",
            enrollTitle: "Set up two-factor",
            enrollSubtitle: "First sign-in for {name} — 2FA is required before you can continue.",
            step1: "Install Google Authenticator or any TOTP app",
            step2: "Scan this QR (or type the key below)",
            step3: "Enter the 6-digit code it shows to confirm",
            manualKey: "Manual key",
            codeFromApp: "Code from the app",
            confirmFinish: "Confirm & finish",
            keepApp: "Keep the app on your phone — you’ll need a fresh code at every login.",
            skewTitle: "Third code in a row didn’t match.",
            skewBody: "This is usually a phone-clock problem, not a typo. In your authenticator app enable time sync (e.g. Google Authenticator → Settings → Time correction), then try the newest code.",
        },
        scope: {
            locationCount: "{count} locations",
            locationCountOne: "1 location",
            fixedLabel: "Fixed scope",
            label: "Scope",
            allLocations: "All locations",
            allMyLocations: "All my locations",
            fleetWide: "Fleet-wide — money is never location-scoped",
        },
        navGroup: {
            operations: "Operations",
            fleet: "Fleet",
            network: "Network",
            money: "Money",
            catalogPeople: "Catalog & people",
            insight: "Insight",
        },
        // Freshness stamp on every polled surface. `freshnessIdle` ("Live") covers
        // the pre-first-success state; these cover the rest.
        freshness: {
            // Announced ONLY on a transition (see FreshnessStamp): the visible counter changes every
            // second and must never be a live region.
            cadence: "auto {seconds}s",
            announceStale: "The data on this screen is out of date.",
            announceOffline: "The backend cannot be reached. Showing the last data received.",
            announceFresh: "Data updated.",
            announceReconnected: "Connection restored. Data updated.",
            checking: "Checking…",
            updatedAgo: "Updated {seconds}s ago",
            offline: "Can’t reach the backend — showing the last data received. Retrying automatically.",
        },
        // ---- fleet vocabularies (FW0–FW2) ----
        fleet: {
            showingRows: "Showing {shown}",
            loadMore: "Load more",
            endOfLog: "End of the log",
            pagingCap: "capped at {max} — narrow by status or location to see further back",
            olderCommands: "{count} older commands",
            pageTitle: "Fleet health",
            pageSub: "“Online” means the booth completed a backend request within the last 60 seconds — so how fresh this page is, is part of the reading.",
            loading: "Loading devices",
            loadingCommands: "Loading commands",
            refreshFailed: "The last refresh failed. These are the figures received before it.",
            colAttention: "Needs attention",
            colActions: "Actions",
            noLocation: "No location",
            lastSeenAgo: "last seen {duration} ago",
            lastSeenNever: "never seen",
            faultCount: "{count} faults",
            configPending: "Config pending",
            filter: {
                label: "Show",
                help: "Filters the rows already on screen — it never hides a booth the console has not loaded.",
                all: "All booths",
                attention: "Needs attention",
                offline: "Offline",
                unsynced: "Holding unsynced sales",
                locked: "Locked",
            },
            showingCount: "Showing {shown} of {total}",
            emptyFilteredTitle: "Nothing matches this filter",
            emptyFilteredBody: "Every booth in this scope is normal on that measure.",
            tableCaption: "Devices and their health",
            summary: {
                online: "Online",
                onlineSub: "of {total} reporting",
                offline: "Offline",
                offlineSub: "no backend request in 60s",
                attention: "Needs attention",
                attentionSub: "faults, holds or drift",
                unsynced: "Unsynced journal",
                unsyncedSub: "records held on {devices} booths",
            },
            tabsLabel: "Device views",
            tabHealth: "Health",
            tabCommands: "Commands",
            sectionResources: "Firmware & resources",
            sectionCommandsDesc: "Every issuance records who, why, and how it ended.",
            sectionIssueDesc: "Commands take effect at the booth and are audited under your name.",
            // What the operator is about to cause, one sentence per command. Stated from the booth's
            // point of view, because that is where the consequence lands.
            commandConsequence: {
                lock: "The booth stops selling until it is unlocked.",
                unlock: "The booth can sell again, unless another hold applies.",
                enter_maintenance: "The booth stops serving customers and shows its maintenance screen.",
                exit_maintenance: "The booth leaves maintenance and returns to normal service.",
                config_reload: "The booth re-pulls its pricing and configuration. Service is not interrupted.",
                display_message: "The message below appears on the booth screen.",
                restart_app: "The controller restarts. The booth is unavailable until it is back up.",
            },
            confirmTargetLabel: "Booth",
            confirmDisruptive: "Anyone at this booth will notice: service stops until this is reversed.",
            confirmAudit: "Recorded against your account, with the reason above.",
            offlineIssueHelp: "This booth is offline. The command is queued and marked “not delivered” if it does not reach the device before its deadline.",
            stillNeeded: "Still needed: {fields}",
            noCommandsHelp: "Commands issued from the console or the ops CLI appear here with their outcome.",
            commandsSub: "Every command issued to a booth, with its delivery and its outcome.",
            commandsSubInFlight: "{count} still in flight — checking every 10 seconds.",
            // The two liveness signals are deliberately NEVER merged: `online` is business
            // connectivity (the device's HTTPS transaction path within 60s) while
            // `telemetryFresh` is MQTT transport health. A unit can transact perfectly with
            // stale telemetry (broker trouble), and showing one number would hide that.
            online: "Online",
            offline: "Offline",
            onlineHelp: "The device completed a backend request in the last 60 seconds.",
            telemetryFresh: "Telemetry live",
            telemetryStale: "Telemetry stale",
            telemetryHelp: "Whether MQTT telemetry arrived in the last 60 seconds. Separate from Online — a device can be transacting normally while its telemetry is stale.",
            // The booth's heartbeat says SAFE_MODE: it sells nothing until a fix reaches it.
            safeMode: "Safe mode",
            deviceStatus: {
                registered: "Registered",
                provisioned: "Provisioned",
                active: "Active",
                inactive: "Inactive",
                locked: "Locked",
                maintenance: "Maintenance",
                retired: "Retired",
            },
            commandStatus: {
                pending: "Pending",
                delivered: "Delivered",
                accepted: "Accepted",
                running: "Running",
                succeeded: "Succeeded",
                failed: "Failed",
                partial: "Partial",
                rejected: "Rejected",
                // Kept distinct: never reached the device vs. accepted then went silent. They call
                // for different responses — re-issue, or go inspect the unit.
                delivery_failed: "Not delivered",
                execution_timeout: "No outcome reported",
            },
            // ALL TWELVE backend CommandTypes, not just the seven the console offers.
            // The palette is gated to what FW2 executes (lib/status.ts), but the LOG shows
            // whatever was issued — including commands sent from the ops CLI or the bench —
            // and an unlabelled one used to render as raw snake_case next to translated rows.
            commandType: {
                lock: "Lock",
                unlock: "Unlock",
                enter_maintenance: "Enter maintenance",
                exit_maintenance: "Exit maintenance",
                config_reload: "Reload config",
                display_message: "Show message",
                restart_app: "Restart",
                // Not offered by the console: accepted by the backend, rejected by FW2
                // firmware until a later phase gives each one a handler.
                upload_diagnostics: "Upload diagnostics",
                check_firmware: "Check firmware",
                force_offline_sync: "Force offline sync",
                rotate_cert: "Rotate certificate",
                rotate_journal_key: "Rotate journal key",
            },
            link: { wifi: "Wi-Fi", lte: "LTE", none: "None", standby: "Standby" },
            cellular: {
                mode: { auto: "Auto (network-assigned)", manual: "Manual APN", none: "No policy (factory seed)" },
                state: {
                    none: "No profile",
                    // Amber, not success: the profile is on trial and auto-reverts if it cannot prove
                    // itself, so calling it adopted would be premature.
                    provisional: "On trial",
                    confirmed: "Confirmed",
                    reverted: "Reverted",
                },
                lkg: { none: "No fallback", factory: "Factory seed (unproven)", confirmed: "Proven fallback" },
                lkgHelp: "What this unit would fall back to if a pushed APN failed. A factory seed has never been proven by a round-trip, so it is the riskiest place to push a new APN.",
                provisionalHelp: "A pushed profile is provisional until the device proves it by reaching the backend over it. If it can’t, the device reverts on its own within about 10 minutes and raises a fault.",
            },
            convergence: {
                label: "Config",
                converged: "Converged",
                pending: "Pending",
                unknown: "Unknown",
                help: "Whether the device is running the configuration the backend currently has for it.",
                unknownHelp: "The device hasn’t reported which config version it runs, or the backend couldn’t resolve the target. This is not a mismatch.",
            },
            faultFlags: { CELLULAR_APN_REVERTED: "APN reverted" },
            // ---- screen copy ----
            emptyTitle: "No devices reporting yet",
            emptyBody: "Devices appear here once they have sent telemetry or completed a backend request.",
            colDevice: "Device",
            colLocation: "Location",
            colLiveness: "Liveness",
            colLink: "Link",
            colCellular: "Cellular",
            colState: "State",
            colFirmware: "Firmware",
            colIssued: "Issued",
            colStatus: "Status",
            colDetail: "Device detail",
            signal: "Signal",
            uptime: "Uptime",
            memory: "Memory",
            timeTrusted: "Clock",
            trusted: "Trusted",
            untrusted: "Untrusted",
            operator: "Operator",
            desiredVersion: "Target version",
            reportedVersion: "Running version",
            cellularState: "Pushed profile",
            cellularFallback: "Fallback",
            cellularApn: "Active APN",
            sectionHealth: "Health & diagnostics",
            sectionConfig: "Configuration",
            sectionCellular: "Cellular policy",
            // Phase 24: why and when the booth last started, its own can-sell verdict and its card reader's. Every code
            // the booth sends is worded here; one with no wording is shown as itself.
            diag: {
                started: "Last start",
                startNumber: "start {boot}",
                resetReason: {
                    power_on: "power switched on",
                    external: "reset pin",
                    software: "restarted itself (an update, going back, or a restart command)",
                    panic: "crashed",
                    int_wdt: "hung (interrupt watchdog)",
                    task_wdt: "hung (task watchdog)",
                    wdt: "hung (watchdog)",
                    deep_sleep: "woke from sleep",
                    brownout: "its supply dipped (brownout)",
                    sdio: "SDIO reset",
                    usb: "USB reset",
                    jtag: "JTAG reset",
                    efuse: "eFuse fault",
                    power_glitch: "power glitch",
                    cpu_lockup: "CPU lockup",
                    unknown: "unknown reason",
                },
                canSell: "Can sell",
                sellYes: "Yes",
                sellNo: "No",
                saleBlocked: {
                    scratch_unavailable: "its sale store is unavailable",
                    tombstones_full: "too many unsettled sales stored",
                    open_marker: "a sale is still being settled",
                    not_calibrated: "not calibrated",
                    no_price: "no price",
                    locked: "locked",
                    maintenance: "in maintenance",
                    network_not_ready: "no network yet",
                    boot_recovery: "settling sales from before its start",
                    valve_not_closing: "the valve may not be closing",
                    updating: "installing an update",
                    backend_not_commissioned: "not commissioned",
                    backend_no_location: "no location assigned",
                    backend_maintenance: "held for maintenance",
                    backend_unknown: "the backend's answer is unknown",
                    mode_unreadable: "its mode could not be read",
                    card_reader_not_ready: "card reader not ready",
                    card_reader_fault: "card reader fault",
                },
                reader: "Card reader",
                readerHealth: { starting: "starting", operational: "working", recovering: "recovering", fault: "fault" },
                readerReady: "ready",
                readerNotReady: "not ready",
            },
            // Phase 24: what the booth's incident log uploaded.
            incidents: {
                title: "Incident log",
                help: "What the booth wrote down itself and sent: each start and why, card reader faults, the modem's resets and lost LTE sessions. Switching it off and on does not erase it.",
                empty: "No incidents received from this booth.",
                colWhen: "When",
                colStart: "Start",
                colWhat: "What",
                colDetail: "Details",
                clockUnsetReported: "clock not set; reported {when}",
                estimated: "about {when}",
                estimatedHelp: "Its clock was not set yet; worked out from the same start's other records or its telemetry.",
                modemPowerUp: "Modem powered up",
                afterUptime: "{uptime} after start {boot}",
                older: "Show older",
                kind: {
                    boot: "Started",
                    reader: "Card reader fault",
                    modem_reset: "Modem reset by the booth",
                    modem_boot: "Modem restarted by itself",
                    ppp_lost: "LTE session lost",
                },
                otherKind: "Other record",
                cause: {
                    no_ack: "a search not acknowledged",
                    refused: "a reconfiguration refused",
                    path_lost: "the reader had reset itself",
                    unanswered: "a command not answered",
                    link: "a link error",
                    chip: "a chip error",
                },
                pulse: {
                    unsaid: "no reason recorded",
                    silent: "the modem stopped answering",
                    radio_dead: "the radio kept failing to register",
                    sim: "the SIM was not ready",
                    path: "the session carried nothing",
                },
                count: "{n} this start",
                board: "board {c} °C",
                modemTemp: "modem {c} °C",
                supply: "modem supply {v} V (lowest {min} V)",
                sinceModem: "{t} after its last modem reset",
                readerCounts: "reader resets {resets}, unanswered {unanswered}, link errors {link}, chip errors {chip}",
            },
            sectionCommands: "Command history",
            sectionIssue: "Issue a command",
            commandLabel: "Command",
            reasonLabel: "Reason",
            reasonHint: "Recorded in the audit trail — say why, not what.",
            messageLabel: "Message",
            messageHint: "Shown on the device screen. ASCII only, up to 64 characters.",
            // Named character, not a generic "invalid input": the offender is usually one pasted
            // curly quote or emoji in otherwise fine text, and an operator cannot see which.
            messageAsciiError: "The device screen can only show plain English letters, digits and punctuation — remove “{char}”.",
            // Unbanked sales the unit is still holding offline.
            journalLabel: "Offline journal",
            journalClear: "All synced",
            journalUnsynced: "{count} unsynced",
            issueAction: "Issue command",
            issued: "Command issued. Watch its status below.",
            confirmTitle: "Issue this command?",
            confirmBody: "{command} will be sent to this device and recorded against your account. Reason: {reason}",
            inFlight: "Still in flight",
            noCommands: "No commands yet.",
            filterStatus: "Status",
            filterAll: "All",
            stampIssued: "issued",
            // "sent", not "delivered" or "published": delivered_at is stamped when the backend
            // hands the command to a transport, which happens even for commands the device never
            // accepts. Paired with the "Not delivered" status it read as a contradiction, and
            // "published" leaked the transport (MQTT today, the HTTPS fallback otherwise).
            stampDelivered: "sent",
            stampAccepted: "accepted",
            stampOutcome: "outcome",
            deliveryFailedHelp: "Never reached the device before its deadline. Re-issue when the unit is back online.",
            executionTimeoutHelp: "The device accepted it, then reported no outcome. Check the unit itself.",
        },
        // Calibration. nl_per_pulse converts flow-meter pulses into litres and therefore into
        // money, which is why a technician measures it at the booth and a MANAGER approves the result.
        // The copy here never promises a booth will sell: this plane sees maintenance locks only, not
        // device status, pricing, location or shift gates.
        calibration: {
            trialsCaption: "Every pour behind this calibration",
            queueSub: "{count} awaiting a decision. A technician is waiting on each one.",
            queueSubEmpty: "Nothing is waiting on you.",
            loading: "Loading proposals",
            reviewAction: "Review",
            waitingFor: "waiting {duration}",
            proposalDesc: "Approving makes this the billing basis for every sale at this booth.",
            validDaysError: "Enter a whole number of days between {min} and {max}.",
            approveDoesNotChange: "Nothing else changes. Device status, pricing, location and shift gates are not visible on this screen.",
            historyHint: "Every record, including the rejected ones",
            pageTitle: "Calibration",
            sectionTitle: "Calibration",
            state: "State",
            stateLabel: {
                valid: "Valid",
                expiringSoon: "Expiring soon",
                expired: "Expired",
                not_yet_effective: "Not yet effective",
                missing: "Never calibrated",
            },
            notBlocking: "Calibration is not blocking this booth. Other checks are not visible here.",
            blocking: "Calibration is blocking this booth.",
            neverCalibrated: "This booth has never been calibrated.",
            neverCalibratedHelp: "It cannot sell water until a technician measures it with a calibrated vessel at the booth.",
            sessionOpen: "{who} has a calibration session open at this booth, started {when}.",
            aTechnician: "A technician",
            activeHeading: "Current factor",
            proposedHeading: "Proposed factor",
            proposalHeading: "Awaiting decision",
            noProposal: "Nothing awaiting a decision.",
            lastRejectedHeading: "Last rejected attempt",
            lastRejectedBody: "{version} was rejected on {when}: {reason}",
            versionCode: "Version",
            factor: "Factor",
            perLitre: "pulses / litre",
            currently: "Currently {ml} mL/pulse · {ppl} pulses / litre",
            quality: "Repeatability",
            acrossTrials: "spread across {n} accepted pours",
            acceptedOfAttempts: "{accepted} accepted of {attempts} pours",
            noEvidence: "No trial data — this record was entered directly, not measured at the booth.",
            method: "Method",
            methodLabel: { single_close: "Single close", creep: "Creep to the mark" },
            referenceLabel: { vessel: "Calibrated vessel", scale: "Scale" },
            valve: "Valve",
            valveDetail: "{close} ms close · {coast} mL coast · {flow} pulses/s",
            measuredBy: "Measured by",
            validity: "Valid",
            firmware: "Firmware",
            decidedBy: "Decided by",
            deviationHelp: "against the current factor",
            noPreviousFactor: "No previous factor to compare against.",
            showTrials: "Show every pour",
            noTrials: "No pours recorded.",
            trialIndex: "#",
            trialKind: "Pour",
            trialTarget: "Target",
            trialMeasured: "Measured",
            trialPulses: "Pulses",
            trialCloses: "Closes",
            trialFactor: "mL/pulse",
            trialVerdict: "Verdict",
            kind: { learning: "Learning", trial: "Trial", rejected_trial: "Rejected" },
            verdict: { at_mark: "At the mark", short: "Short", over: "Over" },
            learningExcluded: "Excluded from the pooled figure — multi-close by design.",
            multiClose: "· not a single close",
            decisionTitle: "Decision",
            approveAction: "Approve",
            rejectAction: "Reject",
            decided: "Recorded.",
            reasonLabel: "Reason",
            reasonHint: "Recorded in the audit trail — say why, not what. Required to reject.",
            rejectNeedsReason: "A reason is required to reject: the technician has to redo the procedure and will read it.",
            validDaysLabel: "Valid for (days)",
            validDaysHint: "Counted from approval, not from when the measurement was taken.",
            confirmApproveTitle: "Approve this calibration?",
            confirmApproveBody: "{version} becomes the billing basis for every sale at this booth, valid for {days} days from now.",
            approveUnblocksCalibration: "Calibration will no longer block sales, and no other maintenance locks remain.",
            approveLeavesLocks: "Calibration will no longer block sales, but this booth stays locked: {causes}.",
            confirmRejectTitle: "Reject this calibration?",
            confirmRejectBody: "The booth keeps its current factor — nothing about how it is running changes. The technician will have to measure again, and will see: {reason}",
            technicianCannotDecide: "A manager or admin approves calibrations. You can see the measurement and its evidence here.",
            settledByYou: "You already {status} this calibration at {when}. The earlier response was lost.",
            settledByOther: "This calibration was already {status} at {when} by {who}.",
            rejectionReasonWas: "Reason given: {reason}",
            status: {
                proposed: "awaiting a decision",
                active: "approved",
                superseded: "superseded",
                rejected: "rejected",
                revoked: "revoked",
                failed: "recorded as failed",
            },
            gate: { min_trials: "Accepted pours", max_spread_bp: "Spread" },
            warning: {
                largeDeviation: "This changes the billing factor by more than 5%.",
                spreadNearGate: "The pours varied more than usual — close to the limit that would refuse them.",
                replacesWorkingFactor: "This booth already has a valid calibration. Approving replaces a factor that is not expired or missing.",
                staleProposal: "This measurement was taken several days ago.",
            },
            lockCause: {
                cleaning_overdue: "Cleaning overdue",
                filter_overdue: "Filter overdue",
                water_test_overdue: "Water test overdue",
                water_test_failed: "Water test failed",
                calibration_expired: "Calibration expired",
                calibration_missing: "Never calibrated",
                calibration_failed: "Calibration failed",
                inspection_required: "Inspection required",
                fault: "Fault",
                manual: "Locked manually",
            },
            historyTitle: "Calibration history",
            historyEmpty: "No calibration records for this booth.",
            colStatus: "Status",
            colWhen: "When",
            showEvidence: "Show pours",
            hideEvidence: "Hide pours",
            queueTitle: "Calibration approvals",
            queueEmpty: "Nothing awaiting a decision.",
            queueEmptyHelp: "Booth calibrations appear here as soon as a technician submits one.",
            awaitingDecision: "Awaiting decision",
            colDevice: "Booth",
            colLocation: "Location",
            colChange: "Change",
            colSpread: "Spread",
            colTrials: "Pours",
            colTechnician: "Measured by",
            colProposed: "Submitted",
        },
        maintenance: {
            dueBoardSub: "Every booth you can see, worst first — the order is the backend’s judgement.",
            dueBoardEmptyHelp: "Booths appear here once they are commissioned.",
            loading: "Loading booths",
            summary: {
                locked: "Held",
                lockedSub: "not selling until cleared",
                calibration: "Calibration blocking",
                calibrationSub: "expired or never measured",
                overdue: "Overdue work",
                overdueSub: "cleaning, filter or water test",
                awaiting: "Awaiting decision",
                awaitingSub: "a technician is waiting",
            },
            moreLocks: "+{count} more",
            dueBoardTitle: "Maintenance",
            dueBoardEmpty: "No booths to show.",
            colDevice: "Booth",
            colLocation: "Location",
            colCalibration: "Calibration",
            colLocks: "Locks",
            colDue: "Next due",
            noLocks: "None",
            overdue: "{what} overdue",
            cleaning: "Cleaning",
            filter: "Filter",
            waterTest: "Water test",
        },
        locks: {
            heldDescription: "This booth refuses sales while any hold is active.",
            clearDescription: "No maintenance hold is stopping this booth.",
            heldCount: "{count} active",
            notHeld: "Not held",
            assertNeedsReason: "A reason is required — it is what the next person reads.",
            title: "Locks",
            none: "No active locks on this booth.",
            colCause: "Cause",
            colSource: "Raised by",
            colSince: "Since",
            colReason: "Reason",
            source: { auto: "Automatic", manual: "By hand" },
            clearAction: "Clear",
            clearTitle: "Clear this lock?",
            confirmClear: "The {cause} hold is lifted. Other locks, if any, stay in place.",
            assertTitle: "Lock this booth",
            assertAction: "Lock",
            assertHelp: "Only the causes a person judges. Overdue holds are raised from the records themselves.",
            confirmAssert: "This booth is held for {cause} until someone clears it.",
            reasonHint: "Recorded in the audit trail — say why, not what.",
            needsCorrectiveRecord: "Needs a passing record before it can be cleared.",
            preconditionWarning: "This cause is only clearable once the corrective state is on record. Without it the request is refused.",
            preconditionFailed: "Refused: the corrective state is not on record yet. File the passing record first, then clear the lock.",
        },
        attachments: {
            photoAlt: "Photo attached to this record",
            addPhoto: "Add a photo to this record",
            unavailable: "Photo attachments are not enabled on this deployment.",
            pending: "Reserved, not uploaded",
            resumable: "The upload did not finish. Choosing a file again resumes it — no second record is created.",
            uploadFailed: "The upload did not complete. Try the same file again.",
            discard: "Discard this upload",
            step: {
                creating: "Reserving…",
                uploading: "Uploading…",
                confirming: "Verifying the file arrived…",
            },
        },
        records: {
            sectionTitle: "Routine records",
            sectionDesc: "Filing a record clears the matching overdue hold in the same step.",
            historyTitle: "Recent records",
            lastRecorded: "Last recorded {when} by {who}",
            nextDueOn: "next due {when}",
            unknownPerformer: "someone unrecorded",
            notesHint: "Anything the next technician should know.",
            lifeDaysHint: "How long this filter is expected to last.",
            kind: {
                cleaning: "Cleaning",
                "filter-change": "Filter change",
                "water-test": "Water test",
                inspection: "Inspection",
            },
            none: "Nothing recorded yet.",
            filed: "Recorded.",
            fileTitle: "Record this work",
            fileAction: "File record",
            colPerformed: "Performed",
            colBy: "By",
            colNextDue: "Next due",
            colResult: "Result",
            colPhotos: "Photos",
            result: { pass: "Pass", fail: "Fail" },
            performerLabel: "Who did the work",
            performerSelf: "Me — {name}",
            performerOther: "Someone else",
            performerHint: "Recorded against your staff account.",
            performerAdminHint: "Admins record work against the person who did it, by name.",
            requiredMark: "(required)",
            stillNeeded: "Still needed: {fields}",
            staffIdLabel: "Staff member",
            staffIdHint: "The staff id of whoever did the work.",
            otherLabel: "Or an outside name",
            otherHint: "Use this when the work was not done by a staff member.",
            performedBy: { nameRequired: "Say who did the work." },
            notesLabel: "Notes",
            reasonLabel: "Reason",
            reasonHint: "Recorded in the audit trail — say why, not what.",
            nextDueHint: "When this is due again. Filing the record clears any overdue hold for it.",
            failLocksBooth: "A failing test holds the booth until a passing one is recorded.",
            field: {
                next_due_at: "Next due",
                cleaning_type: "Cleaning type",
                filter_type: "Filter type",
                filter_serial: "Filter serial",
                expected_life_days: "Expected life (days)",
                method: "Method",
                issue_type: "Issue type",
                findings: "Findings",
                action_taken: "Action taken",
                test_result: "Test result",
                next_action: "Next action",
            },
        },
        // ---- firmware: releases, rollouts, one booth's firmware ----
        firmware: {
            title: "Firmware",
            sub: "Signed releases and their rollouts to the booths. A release arrives only through a signed import; rollouts start here.",
            newRollout: "New rollout",
            loading: "Loading firmware",
            gateBanner: "The rollout of {version} paused itself: {detail}",
            openRollout: "Open the rollout",
            rolloutsTitle: "Rollouts",
            rolloutsHelp: "Newest first. A rollout pauses itself when a stage has more failures than it allows.",
            noRollouts: "No rollouts yet",
            noRolloutsHelp: "Roll a release out from the list of releases below.",
            releasesTitle: "Releases",
            releasesHelp: "Newest first. Deprecate a release to stop new rollouts of it; revoke it to withdraw it for good.",
            noReleases: "No releases yet",
            noReleasesHelp: "Releases arrive through a signed import (on the lab: dpci ota).",
            colVersion: "Version",
            colBuild: "Build",
            colStatus: "Status",
            colStage: "Stage",
            colBooths: "Booths",
            colBooth: "Booth",
            colStarted: "Started",
            colImported: "Imported",
            colActions: "Actions",
            colState: "State",
            colAttempts: "Attempts",
            colDownloaded: "Downloaded",
            colReason: "Reason",
            colUpdated: "Updated",
            colWhen: "When",
            colEvent: "Event",
            colOutcome: "Outcome",
            stageOf: "{index} of {count} · {name}",
            rollOut: "Roll out",
            noBooths: "no booths",
            funnel: {
                waiting: "{n} waiting",
                underWay: "{n} under way",
                proven: "{n} proven",
                failed: "{n} failed",
                wentBack: "{n} went back",
                closed: "{n} closed",
                other: "{n} other",
            },
            rolloutStatus: { active: "Active", paused: "Paused", aborted: "Aborted", completed: "Completed" },
            byGate: "(by its gate)",
            byOperator: "(by an operator)",
            releaseStatus: { available: "Available", deprecated: "Deprecated", revoked: "Revoked" },
            attemptState: {
                pending: "Waiting",
                offered: "Offered",
                downloading: "Downloading",
                staged: "Ready to switch",
                applying: "Switching",
                proven: "Proven",
                failed: "Failed",
                rolled_back: "Rolled back",
                reverted: "Went back",
                cancelled: "Cancelled",
                skipped: "Skipped",
            },
            policy: { any: "Any link", wifi_preferred: "Wi-Fi preferred", wifi_only: "Wi-Fi only" },
            policyHelp: {
                any: "Over Wi-Fi or LTE, whichever the booth is on.",
                wifi_preferred: "Over Wi-Fi; over LTE only after hours without Wi-Fi.",
                wifi_only: "Over Wi-Fi only: a booth on LTE waits.",
            },
            // ---- one rollout ----
            rolloutTitle: "Rollout of {version}",
            rolloutSub: "Started {when} by {who}: {reason}",
            gateTitle: "Its gate paused it",
            gateHelp: "Resume it with a reason, raising the stage's limit if the failures are understood, or abort it.",
            pausedHelp: "Paused by an operator: nothing new is offered until it resumes, and booths keep what they downloaded.",
            factsTitle: "Rollout",
            release: "Release",
            download: "Download",
            idle: "Quiet time before switching",
            inFlight: "Booths at a time",
            urgent: "Urgent",
            urgentYes: "Yes: a booth may take it while its image is in its guard window",
            no: "No",
            stagesTitle: "Stages",
            stageCurrent: "current",
            stageNamed: "{count} named booth(s)",
            stageAll: "every other booth",
            stageGateNone: "no gate",
            stageGateZero: "the first failure pauses the rollout",
            stageGate: "allows {n} failure(s); the next one pauses the rollout",
            stageSoak: "runs at least {duration} before the next stage",
            boothsTitle: "Booths",
            actionsTitle: "Actions",
            pause: "Pause",
            pauseTitle: "Pause this rollout?",
            pauseConsequence: "No booth is offered it until it resumes. Booths keep what they downloaded, and one already switching finishes; its outcome counts.",
            resume: "Resume",
            resumeTitle: "Resume this rollout?",
            resumeConsequence: "Booths are offered it again where they stopped.",
            resumeGateWarning: "Its gate paused it: resumed with the same limit, it pauses again at the gate's next run.",
            resumeLimit: "Failures this stage allows",
            resumeLimitHelp: "Now {n}. Leave it empty to keep it; raise it only when the failures so far are understood.",
            advance: "Start the next stage",
            advanceTitle: "Start the next stage?",
            advanceConsequence: "The next stage starts: {stage}. Its booths are offered the release.",
            advanceForce: "Start it although the current stage has a booth under way or has not run its soak time",
            abort: "Abort",
            abortTitle: "Abort this rollout?",
            abortConsequence: "Booths not yet offered are cancelled, and offers in progress are withdrawn at each booth's next check. An aborted rollout cannot be restarted.",
            abortRevert: "Also send back every booth that installed it and can go back",
            revertedCount: "{n} booth(s) were sent back.",
            reasonLabel: "Reason",
            reasonHint: "Kept in the audit trail: write why, not what.",
            confirmAudit: "Recorded under your name, with this reason.",
            stillNeeded: "Still needed: {fields}",
            // ---- one release ----
            releaseTitle: "Release {version}",
            releaseSub: "Imported {when} by {who}",
            revokedHelp: "Revoked: it cannot be rolled out, and its rollouts were aborted. Booths that installed it keep it until they are sent back or a newer release replaces it.",
            deprecatedHelp: "Deprecated: it cannot be rolled out anew; the rollouts it already had go on.",
            releaseFacts: "Release",
            buildId: "Build (ELF sha256)",
            image: "Image",
            imageSha: "Image sha256",
            hwRevs: "Hardware revisions",
            layout: "Partition layout",
            proveWithin: "Must reach the backend within",
            guard: "Keeps the image it replaced for",
            keyId: "Signing key",
            manifestTitle: "Signed manifest",
            manifestHelp: "Exactly as signed; the fields beside it are copied from it at import.",
            deprecate: "Deprecate",
            deprecateTitle: "Deprecate this release?",
            deprecateConsequence: "It can no longer be rolled out anew. The rollouts it already has go on.",
            revoke: "Revoke",
            revokeTitle: "Revoke this release?",
            revokeConsequence: "Withdrawn for good: its open rollouts are aborted and their offers withdrawn. Booths that installed it keep it until they are sent back or a newer release replaces it.",
            // ---- a new rollout ----
            newTitle: "New rollout",
            newSub: "A release, the booths that take it first, and the gate that pauses it if they fail.",
            noAvailable: "No release can be rolled out: none is available.",
            fieldRelease: "Release",
            fieldReleaseNone: "Choose a release",
            fieldBooths: "Booths",
            fieldBoothsHelp: "The first stage: booths someone can watch. Every other booth can follow in a second stage.",
            boothsFilter: "Filter by serial or location",
            boothsSelected: "{n} selected",
            offline: "offline",
            gateTitleNew: "Gate",
            fieldMaxFailures: "Failures allowed before it pauses itself",
            fieldMaxFailuresHelp: "0: the first failure pauses the rollout. A failure is a booth that failed, rolled back, went back by itself, or fell silent two hours after switching.",
            fieldThenAll: "Then every other booth",
            fieldThenAllHelp: "A second stage with every other active booth, started by an operator once the first has run its soak time.",
            fieldSoak: "Hours the first stage runs before the next may start",
            fieldAllMaxFailures: "Failures the second stage allows",
            deliveryTitle: "Delivery",
            fieldPolicy: "Download",
            fieldIdle: "Minutes a booth stays quiet before it switches",
            fieldUrgent: "Urgent",
            fieldUrgentHelp: "A booth may take it while the image it runs is still in its guard window. For a fix, not a routine release.",
            start: "Start rollout",
            startTitle: "Start this rollout?",
            startConsequence: "{version} is offered now to {count} booth(s).",
            startThenAll: "Every other booth follows when you start the next stage.",
            problem: {
                release: "a release",
                booths: "at least one booth",
                max_failures: "a whole number of failures",
                soak: "a soak time of 0 to 720 hours",
                idle: "a quiet time in whole minutes",
                reason: "a reason",
            },
            // ---- a booth's firmware (the device page's Firmware tab) ----
            tab: "Firmware",
            imagesTitle: "Images",
            running: "Running",
            previous: "Previous",
            canGoBack: "it can go back to it",
            cannotGoBack: "it cannot go back to it now",
            reported: "Reported",
            noReport: "This booth has not reported its firmware yet.",
            safeMode: "Safe mode: this booth's image crashed {crashes} boots in a row, with no image to go back to. It sells nothing and checks for a fix every 5 minutes: roll out a release that does not crash to it.",
            sendBackTitle: "Send back",
            sendBackHelp: "For a booth whose update misbehaves. The rollout it came from goes on for the other booths.",
            sendBack: "Send back to {version}",
            sendBackDialog: "Send this booth back?",
            sendBackConsequence: "At its next check, within minutes, it goes back to {version} once no pour can be cut, and reports how it went.",
            revertBlock: {
                no_report: "It cannot be sent back: it has not reported its firmware yet.",
                pending: "A revert of this booth is already under way.",
                not_revertible: "It cannot be sent back now: it names no image it can go back to.",
            },
            latestRevert: "Latest revert",
            revertStatus: { requested: "Requested", done: "Done", refused: "Refused", withdrawn: "Withdrawn" },
            revertTo: "to {version}",
            rolloutsOfBooth: "Its rollouts",
            noRolloutsOfBooth: "It is in no rollout.",
            historyTitle: "History",
            historyHelp: "What it ran and what its updates came to, newest first.",
            noHistory: "No firmware history yet.",
            event: { running: "Running", attempt: "Update", revert: "Revert", safe_mode: "Safe mode", crash: "Crash" },
            phase: {
                failed: "failed",
                rolled_back: "rolled back",
                inconclusive: "inconclusive",
                reverted: "went back",
                requested: "requested",
                done: "done",
                refused: "refused",
                withdrawn: "withdrawn",
            },
        },
        nav: {
            primary: "Console sections",
            dashboard: "Dashboard",
            shifts: "Shifts",
            variance: "Variance",
            offlineReviews: "Offline reviews",
            salesReview: "Sales review",
            cardStock: "Card stock",
            devices: "Devices & health",
            commands: "Commands",
            maintenance: "Maintenance",
            calibration: "Calibration approvals",
            firmware: "Firmware",
            networkDevices: "Devices & onboarding",
            wallets: "Wallets",
            corrections: "Corrections",
            cardsCustomers: "Cards & customers",
            pricing: "Pricing & tax",
            staffAudit: "Staff & audit",
            reports: "Reports",
        },
        // Role-neutral: one guard covers admin-only, manager-and-admin, and
        // technician-inclusive routes, so the copy must not name a tier.
        denied: {
            title: "You don’t have access to this page",
            body: "Your role doesn’t include this area. If you need it for your work, ask an admin to check your permissions.",
            back: "Go to your start page",
        },
        placeholder: {
            title: "{page}",
            body: "This screen arrives in a later phase. The console shell, your role-based navigation, and location scope are live now.",
        },
        operatorEmpty: {
            title: "Hello {name} — nothing lives here yet",
            titleNoName: "You’re signed in — nothing lives here yet",
            body1: "Your shift tools — punch in, cash sales, card-pack sales, shift close — all run on the dispenser itself. This console is for managers, technicians and admins today.",
            body2: "A “My shift” view (expected cash so far, your card stock) is on the roadmap — it needs a small backend addition first.",
        },
        error: {
            loadFailedTitle: "This screen could not load",
            network: "Can’t reach the console — check the VPN and try again.",
            generic: "Something went wrong. Please try again.",
        },
    },
};
//# sourceMappingURL=en.js.map